import type {
  CatalogProductCreateInput,
  CatalogProductUpdateInput,
} from "@/modules/catalog/domain/contracts";
import { useCategoriesQuery } from "@/modules/categories/presentation/api/queries";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import {
  Button,
  FormInput,
  FormRoot,
  FormSelect,
  Label,
  RHF,
  Textarea,
  zodResolver,
} from "@devhop/ui";
import {
  FileTextIcon,
  PackageIcon,
  TagsIcon,
  ToggleRightIcon,
} from "lucide-react";
import { z } from "zod";
import {
  brandGetLabel,
  brandGetValue,
  brandLookupQueryFn,
  brandLookupQueryKey,
  brandPreloadQueryFn,
} from "../api/brandLookup";
import type { BrandDTO } from "../api/client";
import { CascadingCategorySelect } from "./CascadingCategorySelect";

const SECTION_ACCENT = "#004799"; // Tech Blue — ใช้กับ border ด้านซ้าย

const TYPE_OPTIONS = [
  { value: "part_new", label: "ອາໄຫຼແທ້" },
  { value: "part_oem", label: "ອາໄຫຼທຽບ (OEM)" },
  { value: "part_used", label: "ອາໄຫຼຖອດແທ້" },
  { value: "service", label: "ບໍລິການ" },
];

const STATUS_OPTIONS = [
  { value: "draft", label: "ຮ່າງ" },
  { value: "active", label: "ໃຊ້ງານ" },
  { value: "hidden", label: "ເຊື່ອງ" },
  { value: "archived", label: "ເກັບມ້ຽນ" },
];

const CatalogProductFormSchema = z.object({
  name: z.string().min(1, "ກະລຸນາໃສ່ຊື່ສິນຄ້າ"),
  type: z.enum(["part_new", "part_oem", "part_used", "service"]),
  categoryId: z.string().uuid().optional().or(z.literal("")),
  brandId: z.string().uuid().optional().or(z.literal("")),
  description: z.string().nullable(),
  status: z.enum(["draft", "active", "hidden", "archived"]),
});

export type CatalogProductFormValues = z.infer<typeof CatalogProductFormSchema>;

type CatalogProductFormProps = {
  initialValues?: Partial<CatalogProductFormValues>;
  onSubmit: (
    values: CatalogProductCreateInput | CatalogProductUpdateInput,
  ) => void;
  onCancel?: () => void;
  submitting?: boolean;
  submitLabel?: string;
};

export function CatalogProductForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
  submitLabel = "ບັນທຶກ",
}: CatalogProductFormProps) {
  const methods = RHF.useForm<CatalogProductFormValues>({
    resolver: zodResolver(CatalogProductFormSchema),
    defaultValues: {
      name: initialValues?.name ?? "",
      type: initialValues?.type ?? "part_new",
      categoryId:
        initialValues?.categoryId && initialValues.categoryId !== ""
          ? initialValues.categoryId
          : "",
      brandId:
        initialValues?.brandId && initialValues.brandId !== ""
          ? initialValues.brandId
          : "",
      description: initialValues?.description ?? "",
      status: initialValues?.status ?? "draft",
    },
  });

  const categoriesQuery = useCategoriesQuery({ limit: 1000, offset: 0 });
  const categoryId = methods.watch("categoryId") ?? "";

  const handleSubmit = (vals: CatalogProductFormValues) => {
    onSubmit({
      name: vals.name,
      type: vals.type,
      categoryId:
        vals.categoryId && vals.categoryId !== "" ? vals.categoryId : undefined,
      brandId: vals.brandId && vals.brandId !== "" ? vals.brandId : undefined,
      description: vals.description ?? undefined,
      status: vals.status,
    });
  };

  return (
    <FormRoot<CatalogProductFormValues>
      methods={methods}
      onSubmit={handleSubmit}
      className="flex flex-col gap-0 lg:flex-row lg:gap-8"
    >
      {/* คอลัมน์หลัก: ຂໍ້ມູນພື້ນຖານ, ຫມວດຫມູ່/ແບຣນດ໌, ລາຍລະອຽດ */}
      <div className="min-w-0 flex-1 space-y-6">
        {/* ຂໍ້ມູນພື້ນຖານ */}
        <section
          className="rounded-xl border border-border/60 bg-card p-5 shadow-sm"
          style={{
            borderLeftWidth: "3px",
            borderLeftColor: SECTION_ACCENT,
          }}
        >
          <div className="mb-4 flex items-center gap-2">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#004799]/10 text-[#004799]">
              <PackageIcon className="size-4" aria-hidden />
            </div>
            <h3 className="font-semibold text-foreground text-sm">ຂໍ້ມູນພື້ນຖານ</h3>
          </div>
          <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
            <FormInput name="name" label="ຊື່ສິນຄ້າ" placeholder="ກະລຸນາໃສ່ຊື່" />
            <FormSelect
              name="type"
              label="ປະເພດສິນຄ້າ"
              options={TYPE_OPTIONS}
            />
          </div>
        </section>

        {/* ຫມວດຫມູ່ ແລະ ແບຣນດ໌ */}
        <section
          className="rounded-xl border border-border/60 bg-card p-5 shadow-sm"
          style={{
            borderLeftWidth: "3px",
            borderLeftColor: SECTION_ACCENT,
          }}
        >
          <div className="mb-4 flex items-center gap-2">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#004799]/10 text-[#004799]">
              <TagsIcon className="size-4" aria-hidden />
            </div>
            <h3 className="font-semibold text-foreground text-sm">
              ຫມວດຫມູ່ ແລະ ແບຣນດ໌
            </h3>
          </div>
          <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>ຫມວດຫມູ່</Label>
              <CascadingCategorySelect
                categories={categoriesQuery.data?.data ?? []}
                value={categoryId}
                onChange={(id) =>
                  methods.setValue("categoryId", id, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                isLoading={categoriesQuery.isLoading}
                clearable
                requireLeaf
              />
            </div>
            <FormInfiniteCombobox<BrandDTO>
              name="brandId"
              label="ແບຣນດ໌"
              queryKey={[...brandLookupQueryKey]}
              queryFn={brandLookupQueryFn}
              preloadQueryFn={brandPreloadQueryFn}
              getLabel={brandGetLabel}
              getValue={brandGetValue}
              placeholder="ເລືອກແບຣນດ໌..."
              clearable
            />
          </div>
        </section>

        {/* ລາຍລະອຽດ */}
        <section
          className="rounded-xl border border-border/60 bg-card p-5 shadow-sm"
          style={{
            borderLeftWidth: "3px",
            borderLeftColor: SECTION_ACCENT,
          }}
        >
          <div className="mb-4 flex items-center gap-2">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#004799]/10 text-[#004799]">
              <FileTextIcon className="size-4" aria-hidden />
            </div>
            <h3 className="font-semibold text-foreground text-sm">ລາຍລະອຽດ</h3>
          </div>
          <RHF.Controller
            name="description"
            control={methods.control}
            render={({ field }) => (
              <div className="space-y-2">
                <label
                  htmlFor="catalog-product-description"
                  className="font-medium text-foreground text-sm"
                >
                  ຄຳອະທິບາຍສິນຄ້າ
                </label>
                <Textarea
                  id="catalog-product-description"
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder="ລາຍລະອຽດ ຫຼື ໝາຍເຫດ (ບໍ່ບັງຄັບ)"
                  rows={5}
                  className="min-h-[120px] w-full resize-y rounded-lg border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            )}
          />
        </section>
      </div>

      {/* Sidebar: ສະຖານະ + ปุ่ม (sticky บนจอใหญ่) */}
      <aside className="w-full shrink-0 lg:w-72">
        <div className="space-y-5 lg:sticky lg:top-6">
          <section
            className="rounded-xl border border-border/60 bg-card p-5 shadow-sm"
            style={{
              borderLeftWidth: "3px",
              borderLeftColor: SECTION_ACCENT,
            }}
          >
            <div className="flex items-center gap-2">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-[#004799]/10 text-[#004799]">
                <ToggleRightIcon className="size-4" aria-hidden />
              </div>
              <h3 className="font-semibold text-foreground text-sm">ສະຖານະ</h3>
            </div>
            <div className="mt-4">
              <FormSelect
                name="status"
                label="ສະຖານະການໃຊ້ງານ"
                options={STATUS_OPTIONS}
              />
            </div>
          </section>

          <div className="flex flex-col gap-2 rounded-xl border border-border/60 bg-muted/30 p-4 lg:bg-card lg:shadow-sm">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                ຍົກເລີກ
              </Button>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {submitLabel}
            </Button>
          </div>
        </div>
      </aside>
    </FormRoot>
  );
}
